import { useState, useEffect, useRef } from "react";
import { createChart } from "lightweight-charts";
import {
  Box,
  Autocomplete,
  TextField,
  useTheme,
  IconButton,
  Typography,
  CircularProgress,
  Paper,
  Container,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LoopIcon from "@mui/icons-material/Loop";
import { tokens } from "../../../theme";
import { useFetchingData } from "../../../components/api.js";

// ✅ React Router
import { useLocation } from "react-router-dom";

function FullscreenFlowIntraday() {
  const location = useLocation();

  // ✅ กัน hook แตกชื่อ: ไม่ destructure ตรง ๆ
  const api = typeof useFetchingData === "function" ? useFetchingData() : {};
  const fetcher =
    api?.__fetching_data__ ||
    api?.__fetching_data ||
    api?.fetchData ||
    api?.fetchingData ||
    api?.request ||
    null;

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [symbolsToSelect, setSymbolsToSelect] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [showAutocomplete, setShowAutocomplete] = useState(true);

  const [data, setData] = useState(null);
  const [flowAcc, setFlowAcc] = useState(null);

  const [isZoomed, setIsZoomed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const lineSeriesRef = useRef(null);
  const baselineSeriesRef = useRef(null);
  const previousLogicalRangeRef = useRef(null);
  const chartInitializedRef = useRef(false);

  // ✅ base_api_url แบบคุณ
  const base_api_url =
    process.env.NODE_ENV === "production"
      ? window.location.protocol === "https:"
        ? process.env.REACT_APP_API_HTTPS_URL
        : process.env.REACT_APP_API_HTTP_URL
      : "/application";

  // ---------------- helpers ----------------
  async function callApi(url) {
    if (typeof fetcher === "function") {
      return await fetcher({ url });
    }
    const r = await fetch(url, { method: "GET" });
    const text = await r.text();

    // กัน error: ได้ HTML (เช่น 404 page) แล้ว JSON.parse พัง
    try {
      return JSON.parse(text);
    } catch (e) {
      throw new Error(
        `API did not return JSON. First chars: ${text.slice(0, 40)}`
      );
    }
  }

  function parseUpstreamToRows(jsonData) {
    // รองรับหลายทรง:
    // 1) { data: [...] }
    // 2) { payload: { data: "[{...}]" } }
    // 3) { data: { payload: { data: ... } } } (บาง fetcher ห่อชั้น)
    const candidates = [
      jsonData?.data,
      jsonData,
      jsonData?.payload,
      jsonData?.data?.payload,
      jsonData?.payload?.data,
      jsonData?.data?.payload?.data,
      jsonData?.data?.data,
    ];

    for (const c of candidates) {
      if (!c) continue;

      if (Array.isArray(c)) return c;

      // payload.data เป็น string JSON
      if (typeof c === "string") {
        try {
          const parsed = JSON.parse(c);
          if (Array.isArray(parsed)) return parsed;
        } catch (_) {}
      }

      if (typeof c === "object") {
        if (Array.isArray(c.data)) return c.data;
        if (typeof c.data === "string") {
          try {
            const parsed = JSON.parse(c.data);
            if (Array.isArray(parsed)) return parsed;
          } catch (_) {}
        }
      }
    }

    return null;
  }

  // ✅ FIX TIMEZONE: ถ้า backend ส่งเวลาแบบไม่มี timezone (เช่น "2025-12-19 04:00:00")
  // ให้ถือว่าเป็น UTC แล้วเติม "Z" เพื่อกัน browser ตีความเป็นเวลาเครื่อง (ไทย) ทำให้เหลื่อม 7 ชั่วโมง
  function parseTimeToUnixSeconds(t) {
    if (!t) return null;

    const s0 = String(t).trim().replace(" ", "T");
    const hasTz = /([zZ]|[+-]\d{2}:\d{2})$/.test(s0);
    const s = hasTz ? s0 : `${s0}Z`;

    const ms = Date.parse(s);
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
  }

  function filterAndSortUniqueByTime(arr) {
    if (!Array.isArray(arr)) return [];
    // ลบเวลาซ้ำ + sort
    const map = new Map();
    for (const p of arr) {
      if (!p || !Number.isFinite(p.time)) continue;
      map.set(p.time, p);
    }
    return [...map.values()].sort((a, b) => a.time - b.time);
  }

  // ---------------- URL -> selectedSymbol ----------------
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const symbolFromURL = params.get("symbol");

    if (symbolFromURL) {
      setSelectedSymbol(symbolFromURL.toUpperCase());
      setIsLoading(true);
      setShowAutocomplete(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ---------------- fetch symbols list ----------------
  useEffect(() => {
    let alive = true;

    async function fetchSymbols() {
      try {
        const jsonData = await callApi(`${base_api_url}/realtime_symbols`);
        const list =
          jsonData?.data?.symbols ||
          jsonData?.symbols ||
          jsonData?.data ||
          [];

        if (!alive) return;
        if (Array.isArray(list)) setSymbolsToSelect(list);
        else setSymbolsToSelect([]);
      } catch (e) {
        console.error("fetchSymbols error:", e);
        if (alive) setSymbolsToSelect([]);
      }
    }

    fetchSymbols();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------- create chart ONCE ----------------
  useEffect(() => {
    if (!chartContainerRef.current) return;
    if (chartRef.current) return; // already created

    const el = chartContainerRef.current;

    const chart = createChart(el, {
      width: el.clientWidth || 300,
      height: el.clientHeight || 300,
      timeScale: { timeVisible: true, secondsVisible: true },
      rightPriceScale: { visible: true, borderVisible: false },
      leftPriceScale: { visible: true, borderVisible: false, position: "left" },
      localization: { locale: "th-TH" },
      layout: {
        attributionLogo: false,
        background: { type: 'solid', color: theme.palette.background.default },
        textColor: theme.palette.text.primary,
      },
                     grid: {
         horzLines: { color: theme.palette.divider },
         vertLines: { color: theme.palette.divider },
      },
     
    });

    chartRef.current = chart;

    lineSeriesRef.current = chart.addLineSeries({
      color: colors.whiteChart[200],
      priceScaleId: "right",
    });

    baselineSeriesRef.current = chart.addBaselineSeries({
      priceScaleId: "left",
      baseValue: { type: "price", price: 0 },
      topLineColor: colors.greenChart[100],
      bottomLineColor: colors.redChart[100],
      topFillColor1: "rgba(0,0,0,0)",
      topFillColor2: "rgba(0,0,0,0)",
      bottomFillColor1: "rgba(0,0,0,0)",
      bottomFillColor2: "rgba(0,0,0,0)",
      priceFormat: {
        type: "custom",
        minMove: 1e-8,
        formatter: (price) => {
          const absPrice = Math.abs(price);
          if (absPrice >= 1e9) return (price / 1e9).toFixed(3) + "B";
          if (absPrice >= 1e6) return (price / 1e6).toFixed(1) + "M";
          if (absPrice >= 1e5) return (price / 1e5).toFixed(1) + "K";
          if (absPrice >= 1e4) return (price / 1e4).toFixed(2) + "K";
          if (absPrice >= 1e3) return (price / 1e3).toFixed(3) + "K";
          return Number(price).toFixed(0);
        },
      },
    });

    // detect zoom state
    let minFrom = null;
    let maxTo = null;

    chart.timeScale().subscribeVisibleLogicalRangeChange((rng) => {
      if (!rng) return;

      if (minFrom === null || maxTo === null) {
        minFrom = rng.from;
        maxTo = rng.to;
      }

      const prev = previousLogicalRangeRef.current;
      if (!prev) {
        previousLogicalRangeRef.current = rng;
        return;
      }

      const eps = 1e-6;
      const changed =
        Math.abs(prev.from - rng.from) > eps || Math.abs(prev.to - rng.to) > eps;

      if (changed) {
        const reset = rng.from <= minFrom && rng.to >= maxTo;
        setIsZoomed(!reset);
      }

      previousLogicalRangeRef.current = rng;
    });

    // resize observer
    const ro = new ResizeObserver(() => {
      chart.applyOptions({
        width: el.clientWidth || 300,
        height: el.clientHeight || 300,
      });
      if (!isZoomed) chart.timeScale().fitContent();
    });
    ro.observe(el);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      lineSeriesRef.current = null;
      baselineSeriesRef.current = null;
      chartInitializedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.palette.mode]);

  // ---------------- fetch chart data every 10s ----------------
  useEffect(() => {
    if (!selectedSymbol) return;

    let alive = true;

    async function fetchData() {
      try {
        setIsLoading(true);

        // ✅ ใช้ตัวที่คุณ confirm ว่ามีข้อมูลจริง
        const url = `${base_api_url}/realtime_ws_data?symbol=${encodeURIComponent(
          selectedSymbol.toUpperCase()
        )}`;

        const jsonData = await callApi(url);
        if (!alive) return;

        const rows = parseUpstreamToRows(jsonData);
        if (!Array.isArray(rows)) {
          console.error("No timeseries array in response:", jsonData);
          setData(null);
          setFlowAcc(null);
          return;
        }

        // map -> points
        const pts = [];
        const flw = [];

        for (const item of rows) {
          const t = parseTimeToUnixSeconds(item.time);
          if (t == null) continue;

          const lastVal = Number(item.last);
          const flowVal = Number(item.flow);

          if (Number.isFinite(lastVal)) pts.push({ time: t, value: lastVal });
          if (Number.isFinite(flowVal)) flw.push({ time: t, value: flowVal });
        }

        setData(filterAndSortUniqueByTime(pts));
        setFlowAcc(filterAndSortUniqueByTime(flw));
      } catch (e) {
        console.error("fetchData error:", e);
        if (!alive) return;
        setData(null);
        setFlowAcc(null);
      } finally {
        if (alive) setIsLoading(false);
      }
    }

    fetchData();
    const iv = setInterval(fetchData, 10_000);

    return () => {
      alive = false;
      clearInterval(iv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSymbol]);

  // ---------------- push data into chart ----------------
  useEffect(() => {
    const chart = chartRef.current;
    const line = lineSeriesRef.current;
    const base = baselineSeriesRef.current;
    if (!chart || !line || !base) return;

    // watermark
    chart.applyOptions({
      watermark: {
        visible: true,
        fontSize: 36,
        horzAlign: "center",
        vertAlign: "top",
        color: theme.palette.chart?.watermark || "rgba(255,255,255,0.12)",
        text: selectedSymbol ? selectedSymbol.toUpperCase() : "",
      },
    });

    if (data?.length) line.setData(data);
    if (flowAcc?.length) {
      base.setData(flowAcc);
      base.applyOptions({
        baseValue: { type: "price", price: flowAcc[0]?.value || 0 },
      });
    }

    // fitContent เฉพาะตอนแรก หรือถ้ายังไม่ zoom
    if (!chartInitializedRef.current) {
      chart.timeScale().fitContent();
      chartInitializedRef.current = true;
    } else if (!isZoomed) {
      chart.timeScale().fitContent();
    }
  }, [data, flowAcc, isZoomed, selectedSymbol, theme.palette]);

  const handleResetZoom = () => {
    setIsZoomed(false);
    chartRef.current?.timeScale().fitContent();
  };

  return (
    <Box
      id="main"
      className="container"
      sx={{
        height: "99vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", p: 2 }}>
        <Typography variant="h2">Fullscreen Flow Intraday</Typography>
      </Box>

      <Container
        maxWidth="xl"
        sx={{ flex: 1, display: "flex", flexDirection: "column", p: 0 }}
      >
        <Paper
          sx={{
            overflowY: "hidden",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Box
            className="chartBox2"
            sx={{
              borderRadius: "10px",
              position: "relative",
              height: "80vh",
              width: "100%",
              backgroundColor: "#575b69",
              margin: "2px",
              overflow: "hidden",
            }}
          >
            {showAutocomplete ? (
              <Autocomplete
                disablePortal
                options={symbolsToSelect}
                size="small"
                onChange={(_, newValue) => {
                  if (!newValue) return;
                  setSelectedSymbol(String(newValue).toUpperCase());
                  setIsLoading(true);
                  setShowAutocomplete(false);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    const typedValue = event.target.value.trim();
                    if (!typedValue) return;
                    setSelectedSymbol(typedValue.toUpperCase());
                    setIsLoading(true);
                    setShowAutocomplete(false);
                  }
                }}
                sx={{
                  margin: "1%",
                  width: "25%",
                  zIndex: 10,
                  position: "absolute",
                  left: "11%",
                  top: "8px",
                }}
                renderInput={(params) => <TextField {...params} label="" />}
              />
            ) : (
              <IconButton
                onClick={() => setShowAutocomplete(true)}
                sx={{
                  position: "absolute",
                  left: "11%",
                  top: "8px",
                  padding: "8px",
                  margin: "1%",
                  backgroundColor: "transparent",
                  zIndex: 10,
                  "&:hover": { backgroundColor: "#44495a", color: "white" },
                }}
              >
                <SearchIcon />
              </IconButton>
            )}

            <IconButton
              onClick={handleResetZoom}
              sx={{
                position: "absolute",
                right: "12%",
                top: "8px",
                zIndex: 10,
                backgroundColor: "#575b69",
                padding: "8px",
                margin: "1%",
                "&:hover": { backgroundColor: "transparent" },
              }}
            >
              <LoopIcon />
            </IconButton>

            {isLoading && (
              <Box
                className="loading"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  alignItems: "center",
                  height: "100%",
                  mb: "50px",
                }}
              >
                <CircularProgress sx={{ zIndex: 1111, color: "white" }} />
                <Typography variant="body1" sx={{ color: "white", mt: 1 }}>
                  Searching for{" "}
                  {selectedSymbol ? selectedSymbol.toUpperCase() : ""}
                </Typography>
              </Box>
            )}

            <div
              ref={chartContainerRef}
              style={{
                width: "100%",
                height: "100%",
                position: "relative",
                top: 0,
                left: 0,
                zIndex: 0,
              }}
            />
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default FullscreenFlowIntraday;
